import React, { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiCalendar } from 'react-icons/fi';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import palette from '../../lib/styles/palette';
import categoryMap from '../../lib/def/category.json';
import Loading from '../common/Loading';
import { TableContainer } from '../common/Container';
import MonthPicker from '../sales/MonthPicker';
import moment from 'moment';

const DashboardBlock = styled.div`
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
  padding: 12px 8px 40px;
`;

const DashboardHeader = styled.div`
  width: 100%;
  padding: 20px 12px 18px;
  margin-bottom: 24px;
  border-bottom: 1px solid ${palette.webGray[15]};
`;

const DashboardTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${palette.webBlack[9]};
  margin: 0;
`;

const DashboardSubtitle = styled.p`
  font-size: 0.9rem;
  color: ${palette.webGray[6]};
  margin: 6px 0 0 0;
`;

const MainSection = styled.section`
  margin-top: 32px;
  margin-bottom: 80px;
`;

const SectionTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: ${palette.webBlack[9]};
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 4px;
    height: 18px;
    border-radius: 999px;
    background: linear-gradient(180deg, #0ca678, #1971c2);
  }
`;

const SectionCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  margin-bottom: 20px;
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const NavTitle = styled.span`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${palette.webBlack[9]};
`;

const MonthBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const YearBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const CalendarIcon = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  color: ${palette.webGray[7]};
  transition: color 0.15s;
  
  &:hover {
    color: ${palette.webBlack[9]};
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

/* YearPicker 스타일 */
const YearPickerWrapper = styled.div`
  width: 18rem;
  font-family: 'Helvetica Neue';
  position: absolute;
  background: white;
  transform: translateY(55%);
  padding: 0 3px;
  border: 0.5px solid ${palette.webGray[18]};
  border-radius: 0.5rem;
  z-index: 10;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.1);
`;

const YearPickerHeader = styled.div`
  padding: 1rem 10px;
  display: flex;
  justify-content: space-between;
  font-weight: 700;
  align-items: center;
  border-bottom: 1.5px solid ${palette.webGray[18]};
`;

const YearPickerControl = styled.div`
  svg {
    font-size: 20pt;
    vertical-align: middle;
    cursor: pointer;
    color: ${palette.webGray[7]};
    transition: color 0.15s;
    
    &:hover {
      color: ${palette.webBlack[9]};
    }
  }
`;

const YearPickerYear = styled.div`
  font-size: 14pt;
`;

const YearPickerBody = styled.div`
  padding: 15px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-wrap: wrap;
  max-height: 300px;
  overflow-y: auto;
`;

const YearPickerItem = styled.div`
  width: 30%;
  font-size: 11pt;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 6px;
`;

const YearSelectBox = styled.div`
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 50px;
  transition: all 0.15s;
  
  &:hover {
    color: ${palette.webCyan[6]};
    ${(props) =>
      props.selected &&
      `
      color: ${palette.white};
    `}
  }
  ${(props) =>
    props.selected &&
    `
    background: ${palette.webBlack};
    border-radius: 50%;
    color: ${palette.white};
  `}
`;

const NavButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const NavBtn = styled.button`
  padding: 8px 16px;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${palette.webBlack[9]};
  background: ${palette.webGray[2]};
  border: 1px solid ${palette.webGray[14]};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${palette.webGray[3]};
    border-color: ${palette.webGray[12]};
  }
`;

const ChartWrapper = styled.div`
  width: 100%;
  margin: 16px 0;
`;

const ChartLayout = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
`;

const ChartYAxis = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 4px 8px 24px 0;
  font-size: 0.7rem;
  color: ${palette.webGray[6]};
  font-variant-numeric: tabular-nums;
  min-width: 48px;
`;

const ChartSvg = styled.svg`
  flex: 1;
  min-width: 0;
  height: 200px;
  overflow: visible;
`;

const ChartLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  padding-left: 56px;
  font-size: 0.7rem;
  color: ${palette.webGray[6]};
`;

const ChartLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-top: 12px;
  font-size: 0.8rem;
  color: ${palette.webGray[7]};
`;

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const LegendDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => props.color || palette.webCyan[5]};
`;

const TableScroll = styled.div`
  overflow-x: auto;
  margin: 0 -4px;
`;

const DataTable = styled.table`
  width: 100%;
  min-width: 480px;
  border-collapse: collapse;
  font-size: 0.72rem;

  thead {
    background: ${palette.webGray[2]};
  }

  th,
  td {
    padding: 6px 8px;
    border-bottom: 1px solid ${palette.webGray[15]};
    text-align: right;
  }

  td.num {
    font-variant-numeric: tabular-nums;
  }

  th:first-child,
  td:first-child {
    text-align: left;
    font-weight: 500;
  }

  tbody tr:hover {
    background: ${palette.webGray[1]};
  }

  .total-row,
  .total-col {
    font-weight: 600;
    background: ${palette.webGray[2]};
  }

  .total-cell {
    font-weight: 700;
  }

  &.month-table th:first-child,
  &.month-table td:first-child {
    min-width: 140px;
  }
`;

const EmptyText = styled.div`
  font-size: 0.9rem;
  color: ${palette.webGray[6]};
  padding: 24px;
  text-align: center;
`;

/* 달력 */
const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 1px;
  background: ${palette.webGray[14]};
  border: 1px solid ${palette.webGray[14]};
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const CalendarHeader = styled.div`
  display: contents;
  & > div {
    background: ${palette.webGray[3]};
    padding: 10px;
    font-size: 0.8rem;
    font-weight: 600;
    color: ${palette.webGray[8]};
    text-align: center;
    min-width: 0;
  }
`;

const CalendarCell = styled.div`
  background: #fff;
  min-height: 100px;
  min-width: 0;
  padding: 8px;
  font-size: 0.8rem;
  overflow: hidden;
  cursor: pointer;

  &.empty {
    background: ${palette.webGray[1]};
    cursor: default;
  }

  &.today {
    border: 1px solid rgba(12, 166, 120, 0.5);
  }
`;

const CalendarDate = styled.div`
  font-weight: 600;
  font-size: 0.85rem;
  color: ${palette.webBlack[9]};
  margin-bottom: 6px;
`;

const CalendarTotal = styled.div`
  font-size: 0.72rem;
  font-weight: 500;
  color: ${palette.webGray[7]};
  margin-bottom: 6px;
  text-align: center;
`;

const CalendarClubList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid ${palette.webGray[14]};
  min-height: 48px;
`;

const CalendarClubItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.72rem;
  color: ${palette.webGray[7]};
  padding: 2px 0;
`;

const CalendarClubDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) => props.$color || palette.webGray[6]};
  flex-shrink: 0;
`;

const CalendarClubName = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const CalendarClubAmount = styled.span`
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

/* 일별 상세 모달 */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`;

const ModalContent = styled.div`
  background: #fff;
  width: 420px;
  max-width: 90%;
  max-height: 75%;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${palette.webGray[14]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1rem;
  font-weight: 600;
  color: ${palette.webBlack[9]};
`;

const ModalBody = styled.div`
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
`;

const ModalClose = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.1rem;
  color: ${palette.webGray[7]};
  &:hover {
    color: ${palette.webBlack[9]};
  }
`;

const ModalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  padding: 4px 0;
`;

const ModalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  color: ${palette.webGray[8]};
  line-height: 1.4;
`;

const ModalTotal = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${palette.webBlack[9]};
  margin-top: 6px;
`;

const ModalSectionTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${palette.webBlack[9]};
  margin: 12px 0 8px;
`;

const ModalDivider = styled.div`
  height: 1px;
  background: ${palette.webGray[14]};
  margin: 10px 0;
`;

const ModalClubBlock = styled.div`
  padding: 10px 12px 12px;
  border-radius: 10px;
  background: ${palette.webGray[1]};
  & + & {
    margin-top: 20px;
  }
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
`;

const DailyDataSection = styled.div`
  padding: 20px 0;
`;

/* Skeleton UI */
const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(
    90deg,
    ${palette.webGray[1]} 0px,
    ${palette.webGray[2]} 40px,
    ${palette.webGray[1]} 80px
  );
  background-size: 1000px 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: 4px;
`;

const SkeletonBox = styled(SkeletonBase)`
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '20px'};
  margin-bottom: ${(props) => props.marginBottom || '8px'};
`;

const SkeletonChart = styled(SkeletonBase)`
  width: 100%;
  height: 200px;
  margin: 16px 0;
  border-radius: 8px;
`;

const SkeletonTable = styled.div`
  margin-top: 16px;
`;

const SkeletonTableRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
`;

const SkeletonCalendar = styled(SkeletonBase)`
  width: 100%;
  height: 400px;
  border-radius: 8px;
  margin-top: 24px;
`;

const YearSkeleton = () => (
  <>
    <SkeletonChart />
    <SkeletonTable>
      <SkeletonBox height="24px" width="100%" marginBottom="12px" />
      {[1, 2, 3, 4, 5].map((i) => (
        <SkeletonTableRow key={i}>
          <SkeletonBox height="20px" width="120px" />
          {Array.from({ length: 12 }).map((_, j) => (
            <SkeletonBox key={j} height="20px" width="60px" />
          ))}
          <SkeletonBox height="20px" width="80px" />
        </SkeletonTableRow>
      ))}
      <SkeletonTableRow>
        <SkeletonBox height="24px" width="120px" />
        {Array.from({ length: 12 }).map((_, j) => (
          <SkeletonBox key={j} height="24px" width="60px" />
        ))}
        <SkeletonBox height="24px" width="80px" />
      </SkeletonTableRow>
    </SkeletonTable>
  </>
);

const MonthSkeleton = () => (
  <DailyDataSection>
    <SkeletonChart />
    <SkeletonTable style={{ marginTop: '24px' }}>
      <SkeletonBox height="24px" width="100%" marginBottom="12px" />
      {[1, 2, 3, 4, 5].map((i) => (
        <SkeletonTableRow key={i}>
          <SkeletonBox height="20px" width="140px" />
          {Array.from({ length: 7 }).map((_, j) => (
            <SkeletonBox key={j} height="20px" width="60px" />
          ))}
          <SkeletonBox height="20px" width="80px" />
        </SkeletonTableRow>
      ))}
    </SkeletonTable>
    <SkeletonCalendar />
  </DailyDataSection>
);

const COLORS = [
  '#0ca678',
  '#1971c2',
  '#e8590c',
  '#862e9c',
  '#2b8a3e',
  '#c92a2a',
];

const formatYAxis = (max) => {
  if (!max || max <= 0) return ['0'];
  const step = max <= 50000000 ? 10000000 : max <= 200000000 ? 50000000 : 100000000;
  const ticks = [];
  for (let v = 0; v <= max; v += step) {
    ticks.push(v);
  }
  if (ticks[ticks.length - 1] < max) ticks.push(max);
  return ticks;
};

const formatYLabel = (n) => {
  if (n >= 100000000) return `${(n / 100000000).toFixed(0)}억`;
  if (n >= 10000) return `${(n / 10000).toFixed(0)}만`;
  return n.toLocaleString();
};

const CenterDashboardComponent = ({
  yearData,
  monthData,
  selectedYear,
  selectedMonth,
  monthSectionYear,
  dayDetail,
  dayDetailLoading,
  dayDetailError,
  loading,
  yearLoading,
  monthLoading,
  dataError,
  onYearPrev,
  onYearNext,
  onMonthPrev,
  onMonthNext,
  onChangeMonth,
  onChangeYear,
  onRequestDayDetail,
}) => {
  const [monthPickerVisible, setMonthPickerVisible] = useState(false);
  const [yearPickerVisible, setYearPickerVisible] = useState(false);
  const [yearPickerStartYear, setYearPickerStartYear] = useState(selectedYear);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [selectedDayLabel, setSelectedDayLabel] = useState('');
  
  const currentDate = moment(`${monthSectionYear}-${selectedMonth}`).toDate();
  
  const handleToggleMonthPicker = () => {
    setMonthPickerVisible((prev) => !prev);
  };
  
  const handleChangeMonth = (dateString) => {
    const date = moment(dateString);
    onChangeMonth(date.year(), date.month() + 1);
    // MonthPicker의 onSelectMonth에서 이미 onToggleMonthPicker()를 호출하므로 여기서는 호출하지 않음
  };
  
  const handleClickDay = async (cell) => {
    if (cell.empty) return;
    const label = `${monthSectionYear}년 ${selectedMonth}월 ${cell.day}일`;
    setSelectedDayLabel(label);
    setDayModalOpen(true);
    await onRequestDayDetail({ year: monthSectionYear, month: selectedMonth, day: cell.day });
  };
  
  const closeDayModal = () => {
    setDayModalOpen(false);
    setSelectedDayLabel('');
  };
  
  const handleToggleYearPicker = () => {
    setYearPickerVisible((prev) => {
      if (!prev) {
        setYearPickerStartYear(selectedYear);
      }
      return !prev;
    });
  };
  
  const handleChangeYear = (year) => {
    onChangeYear(year);
    setYearPickerVisible(false);
  };
  
  // 년도 목록 생성 (현재 년도 기준 ±10년)
  const generateYearList = () => {
    const currentYear = new Date().getFullYear();
    const startYear = Math.floor(yearPickerStartYear / 10) * 10;
    const years = [];
    for (let i = startYear; i < startYear + 12; i++) {
      years.push(i);
    }
    return years;
  };
  const monthLabels = useMemo(
    () =>
      [
        '1월', '2월', '3월', '4월', '5월', '6월',
        '7월', '8월', '9월', '10월', '11월', '12월',
      ],
    [],
  );

  const yearTableData = useMemo(() => {
    if (!yearData || yearData.length === 0) return null;
    const rowTotals = yearData.map((c) =>
      c.monthlySales.reduce((a, b) => a + b, 0),
    );
    const colTotals = Array.from({ length: 12 }, (_, m) =>
      yearData.reduce((s, c) => s + c.monthlySales[m], 0),
    );
    const grandTotal = colTotals.reduce((a, b) => a + b, 0);
    return { rowTotals, colTotals, grandTotal };
  }, [yearData]);

  const monthTableData = useMemo(() => {
    if (!monthData || monthData.length === 0) return null;
    const daysInMonth = monthData[0].dailySales.length;
    const rowTotals = monthData.map((c) =>
      c.dailySales.reduce((a, b) => a + b, 0),
    );
    const colTotals = Array.from({ length: daysInMonth }, (_, d) =>
      monthData.reduce((s, c) => s + c.dailySales[d], 0),
    );
    const grandTotal = rowTotals.reduce((a, b) => a + b, 0);
    return { rowTotals, colTotals, grandTotal, daysInMonth };
  }, [monthData]);

  const calendarCells = useMemo(() => {
    if (!monthData || monthData.length === 0) return [];
    const year = monthSectionYear;
    const month = selectedMonth - 1;
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startOffset = first.getDay();
    const daysInMonth = last.getDate();

    const cells = [];
    for (let i = 0; i < startOffset; i++) {
      cells.push({ empty: true });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dayTotal = monthData.reduce(
        (s, c) => s + c.dailySales[d - 1],
        0,
      );
      const dayBreakdown = monthData.map((c, idx) => ({
        name: c.name,
        amount: c.dailySales[d - 1],
        color: COLORS[idx % COLORS.length],
      }));
      const isToday =
        new Date().getFullYear() === year &&
        new Date().getMonth() === month &&
        new Date().getDate() === d;
      cells.push({
        empty: false,
        day: d,
        total: dayTotal,
        breakdown: dayBreakdown,
        isToday,
      });
    }
    const remainder = cells.length % 7;
    if (remainder !== 0) {
      for (let i = 0; i < 7 - remainder; i++) {
        cells.push({ empty: true });
      }
    }
    return cells;
  }, [monthData, monthSectionYear, selectedMonth]);

  const maxMonthly = useMemo(() => {
    if (!yearData || yearData.length === 0) return 1;
    let max = 0;
    yearData.forEach((c) => {
      c.monthlySales.forEach((v) => {
        if (v > max) max = v;
      });
    });
    return max || 1;
  }, [yearData]);

  const maxDaily = useMemo(() => {
    if (!monthData || monthData.length === 0) return 1;
    let max = 0;
    monthData.forEach((c) => {
      c.dailySales.forEach((v) => {
        if (v > max) max = v;
      });
    });
    return max || 1;
  }, [monthData]);

  if (loading) {
    return (
      <DashboardBlock>
        <TableContainer>
          <Loading />
        </TableContainer>
      </DashboardBlock>
    );
  }

  return (
    <>
    <DashboardBlock>
      {/* <DashboardHeader>
        <DashboardTitle>센터 대시보드</DashboardTitle>
        <DashboardSubtitle>
          지점별 매출을 년도·월별로 확인할 수 있습니다.
        </DashboardSubtitle>
      </DashboardHeader> */}

      {dataError && (
        <div style={{ padding: '12px 0', color: '#e03131', fontSize: '0.9rem' }}>
          {dataError}
        </div>
      )}

      {/* 1. 년도별 데이터 */}
      <MainSection>
        <SectionTitle>년도별 데이터</SectionTitle>

        <SectionCard>
          <NavRow>
            <YearBlock>
              <NavTitle>{selectedYear}년</NavTitle>
              <CalendarIcon onClick={handleToggleYearPicker}>
                <FiCalendar />
              </CalendarIcon>
              {yearPickerVisible && (
                <YearPickerWrapper>
                  <YearPickerHeader>
                    <YearPickerControl onClick={() => setYearPickerStartYear((y) => y - 12)}>
                      <MdKeyboardArrowLeft />
                    </YearPickerControl>
                    <YearPickerYear>
                      {Math.floor(yearPickerStartYear / 10) * 10} - {Math.floor(yearPickerStartYear / 10) * 10 + 11}
                    </YearPickerYear>
                    <YearPickerControl onClick={() => setYearPickerStartYear((y) => y + 12)}>
                      <MdKeyboardArrowRight />
                    </YearPickerControl>
                  </YearPickerHeader>
                  <YearPickerBody>
                    {generateYearList().map((year) => (
                      <YearPickerItem key={year} onClick={() => handleChangeYear(year)}>
                        <YearSelectBox selected={year === selectedYear}>
                          {year}
                        </YearSelectBox>
                      </YearPickerItem>
                    ))}
                  </YearPickerBody>
                </YearPickerWrapper>
              )}
            </YearBlock>
            <NavButtons>
              <NavBtn type="button" onClick={onYearPrev}>
                이전
              </NavBtn>
              <NavBtn type="button" onClick={onYearNext}>
                다음
              </NavBtn>
            </NavButtons>
          </NavRow>

          {yearLoading ? (
            <YearSkeleton />
          ) : yearData && yearData.length > 0 ? (
            <>
              <ChartWrapper>
                <ChartLayout>
                  <ChartYAxis>
                    {formatYAxis(maxMonthly)
                      .slice()
                      .reverse()
                      .map((v) => (
                        <span key={v}>{formatYLabel(v)}</span>
                      ))}
                  </ChartYAxis>
                  <ChartSvg viewBox="0 0 100 40" preserveAspectRatio="none">
                    <defs>
                      {yearData.map((row, idx) => {
                        const color = COLORS[idx % COLORS.length];
                        return (
                          <linearGradient
                            key={row.clubID || row.name}
                            id={`yearLine-${idx}`}
                            x1="0"
                            y1="0"
                            x2="1"
                            y2="0"
                          >
                            <stop offset="0%" stopColor={color} stopOpacity="1" />
                            <stop offset="100%" stopColor={color} stopOpacity="1" />
                          </linearGradient>
                        );
                      })}
                    </defs>
                    {yearData.map((row, idx) => {
                      const pts = row.monthlySales.map((v, i) => {
                        const x = (i / 11) * 100;
                        const y = 35 - (v / maxMonthly) * 30;
                        return { x, y };
                      });
                      const d =
                        'M ' +
                        pts.map((p) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' L ');
                      return (
                        <path
                          key={row.clubID || row.name}
                          d={d}
                          fill="none"
                          stroke={`url(#yearLine-${idx})`}
                          strokeWidth={0.22}
                          strokeLinejoin="round"
                          strokeLinecap="round"
                          opacity={0.9}
                        />
                      );
                    })}
                  </ChartSvg>
                </ChartLayout>
                <ChartLabels>
                  {monthLabels.map((l, i) => (
                    <span key={i}>{l}</span>
                  ))}
                </ChartLabels>
                <ChartLegend>
                  {yearData.map((row, idx) => (
                    <LegendItem key={row.clubID || row.name}>
                      <LegendDot color={COLORS[idx % COLORS.length]} />
                      <span>{row.name}</span>
                    </LegendItem>
                  ))}
                </ChartLegend>
              </ChartWrapper>

              <TableScroll>
                <DataTable>
                  <thead>
                    <tr>
                      <th>지점명</th>
                      {monthLabels.map((l, i) => (
                        <th key={i}>{l}</th>
                      ))}
                      <th className="total-col">합계</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearData.map((row, r) => (
                      <tr key={row.clubID || row.name}>
                        <td>{row.name}</td>
                        {row.monthlySales.map((v, m) => (
                          <td key={m} className="num">{v.toLocaleString()}</td>
                        ))}
                        <td className="total-row num">
                          {yearTableData.rowTotals[r].toLocaleString()}
                        </td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td className="total-cell">합계</td>
                      {yearTableData.colTotals.map((v, m) => (
                        <td key={m} className="num">{v.toLocaleString()}</td>
                      ))}
                      <td className="total-cell num">
                        {yearTableData.grandTotal.toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </DataTable>
              </TableScroll>
            </>
          ) : (
            <EmptyText>표시할 데이터가 없습니다.</EmptyText>
          )}
        </SectionCard>
      </MainSection>

      {/* 2. 월별 데이터 */}
      <MainSection>
        <SectionTitle>월별 데이터</SectionTitle>

        <SectionCard>
          <NavRow>
            <MonthBlock>
              <NavTitle>
                {monthSectionYear}년 {selectedMonth}월
              </NavTitle>
              <CalendarIcon onClick={handleToggleMonthPicker}>
                <FiCalendar />
              </CalendarIcon>
              <MonthPicker
                visible={monthPickerVisible}
                onChangeMonth={handleChangeMonth}
                date={currentDate}
                onToggleMonthPicker={handleToggleMonthPicker}
              />
            </MonthBlock>
            <NavButtons>
              <NavBtn type="button" onClick={onMonthPrev}>
                이전
              </NavBtn>
              <NavBtn type="button" onClick={onMonthNext}>
                다음
              </NavBtn>
            </NavButtons>
          </NavRow>

          {monthLoading ? (
            <MonthSkeleton />
          ) : monthData && monthData.length > 0 ? (
            <DailyDataSection>
              {/* 월별 그래프 */}
              <div style={{ marginBottom: 24 }}>
                <div
                  style={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    marginBottom: 10,
                    color: palette.webBlack[9],
                  }}
                >
                  일별 매출 추이
                </div>
                <ChartWrapper>
                  <ChartLayout>
                    <ChartYAxis>
                      {formatYAxis(maxDaily)
                        .slice()
                        .reverse()
                        .map((v) => (
                          <span key={v}>{formatYLabel(v)}</span>
                        ))}
                    </ChartYAxis>
                    <ChartSvg viewBox="0 0 100 40" preserveAspectRatio="none">
                      <defs>
                        {monthData.map((row, idx) => {
                          const color = COLORS[idx % COLORS.length];
                          return (
                            <linearGradient
                              key={row.clubID || row.name}
                              id={`monthLine-${idx}`}
                              x1="0"
                              y1="0"
                              x2="1"
                              y2="0"
                            >
                              <stop
                                offset="0%"
                                stopColor={color}
                                stopOpacity="1"
                              />
                              <stop
                                offset="100%"
                                stopColor={color}
                                stopOpacity="1"
                              />
                            </linearGradient>
                          );
                        })}
                      </defs>
                      {monthData.map((row, idx) => {
                        const n = row.dailySales.length;
                        const pts = row.dailySales.map((v, i) => {
                          const x = (i / (n - 1 || 1)) * 100;
                          const y = 35 - (v / maxDaily) * 30;
                          return { x, y };
                        });
                        const d =
                          'M ' +
                          pts
                            .map((p) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
                            .join(' L ');
                        return (
                          <path
                            key={row.clubID || row.name}
                            d={d}
                            fill="none"
                            stroke={`url(#monthLine-${idx})`}
                            strokeWidth={0.22}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            opacity={0.9}
                          />
                        );
                      })}
                    </ChartSvg>
                  </ChartLayout>
                  <ChartLabels>
                    {(() => {
                      const days = monthTableData.daysInMonth;
                      const labels = [1];
                      const step = Math.max(1, Math.floor(days / 6));
                      for (let d = step; d < days; d += step) {
                        labels.push(d);
                      }
                      if (labels[labels.length - 1] !== days) {
                        labels.push(days);
                      }
                      return labels.map((d) => (
                        <span key={d}>{d}일</span>
                      ));
                    })()}
                  </ChartLabels>
                  <ChartLegend>
                    {monthData.map((row, idx) => (
                      <LegendItem key={row.clubID || row.name}>
                        <LegendDot color={COLORS[idx % COLORS.length]} />
                        <span>{row.name}</span>
                      </LegendItem>
                    ))}
                  </ChartLegend>
                </ChartWrapper>
              </div>

              {/* 월별 표 */}
              <div>
                <div
                  style={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    marginBottom: 10,
                    color: palette.webBlack[9],
                  }}
                >
                  일별 매출 표
                </div>
                <TableScroll>
                  <DataTable className="month-table">
                    <thead>
                      <tr>
                        <th>지점명</th>
                        {Array.from(
                          { length: monthTableData.daysInMonth },
                          (_, i) => (
                            <th key={i}>{i + 1}일</th>
                          ),
                        )}
                        <th className="total-col">합계</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthData.map((row, r) => (
                        <tr key={row.clubID || row.name}>
                          <td>{row.name}</td>
                          {row.dailySales.map((v, d) => (
                            <td key={d} className="num">{v.toLocaleString()}</td>
                          ))}
                          <td className="total-row num">
                            {monthTableData.rowTotals[r].toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      <tr className="total-row">
                        <td className="total-cell">합계</td>
                        {monthTableData.colTotals.map((v, d) => (
                          <td key={d} className="num">{v.toLocaleString()}</td>
                        ))}
                        <td className="total-cell num">
                          {monthTableData.grandTotal.toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </DataTable>
                </TableScroll>
              </div>

              {/* 달력 */}
              <div style={{ marginTop: 24 }}>
                <div
                  style={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    marginBottom: 10,
                    color: palette.webBlack[9],
                  }}
                >
                  달력
                </div>
                <CalendarGrid>
                  <CalendarHeader>
                    {['일', '월', '화', '수', '목', '금', '토'].map((d) => (
                      <div key={d}>{d}</div>
                    ))}
                  </CalendarHeader>
                  {calendarCells.map((cell, i) => (
                    <CalendarCell
                      key={i}
                      className={
                        cell.empty ? 'empty' : cell.isToday ? 'today' : ''
                      }
                      onClick={() => handleClickDay(cell)}
                    >
                      {!cell.empty && (
                        <>
                          <CalendarDate>{cell.day}</CalendarDate>
                          <CalendarTotal>
                            {cell.total > 0
                              ? `${cell.total.toLocaleString()}원`
                              : '-'}
                          </CalendarTotal>
                          {cell.total > 0 && (
                            <CalendarClubList>
                              {cell.breakdown.map((b) => (
                                <CalendarClubItem key={b.name}>
                                  <CalendarClubName>
                                    <CalendarClubDot $color={b.color} />
                                    {b.name.length > 8 ? `${b.name.slice(0, 8)}...` : b.name}
                                  </CalendarClubName>
                                  <CalendarClubAmount>
                                    {b.amount.toLocaleString()}원
                                  </CalendarClubAmount>
                                </CalendarClubItem>
                              ))}
                            </CalendarClubList>
                          )}
                        </>
                      )}
                    </CalendarCell>
                  ))}
                </CalendarGrid>
              </div>
            </DailyDataSection>
          ) : (
            <EmptyText>표시할 데이터가 없습니다.</EmptyText>
          )}
        </SectionCard>
      </MainSection>
    </DashboardBlock>
    
    {dayModalOpen && (
      <ModalOverlay onClick={closeDayModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <span>{selectedDayLabel}</span>
            <ModalClose onClick={closeDayModal}>×</ModalClose>
          </ModalHeader>
          <ModalBody>
            {dayDetailLoading && <div style={{ padding: '12px 0' }}>로딩 중...</div>}
            {dayDetailError && (
              <div style={{ padding: '12px 0', color: '#e03131' }}>{dayDetailError}</div>
            )}
            {!dayDetailLoading && !dayDetailError && dayDetail && (
              <>
                <ModalTotal>
                  총합 {dayDetail.clubs.reduce((s, c) => s + (c.total || 0), 0).toLocaleString()}원
                </ModalTotal>
                <ModalList>
                  {dayDetail.clubs.map((club) => (
                    <ModalClubBlock key={club.clubID}>
                      <ModalRow style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 4 }}>
                        <span>{club.name}</span>
                        <span>{(club.total || 0).toLocaleString()}원</span>
                      </ModalRow>
                      {club.membershipCategories && club.membershipCategories.length > 0 && (
                        <>
                          <ModalSectionTitle>회원권 (카테고리별)</ModalSectionTitle>
                          {club.membershipCategories.map((cat) => (
                            <ModalRow key={`${club.clubID}-${cat.category}`}>
                              <span>
                                · {categoryMap[String(cat.category)] || cat.category || '카테고리 없음'}
                              </span>
                              <span>{(cat.total || 0).toLocaleString()}원</span>
                            </ModalRow>
                          ))}
                        </>
                      )}
                      <ModalDivider />
                      <ModalSectionTitle>기타</ModalSectionTitle>
                      <ModalRow>
                        <span>락커</span>
                        <span>{(club.lockerTotal || 0).toLocaleString()}원</span>
                      </ModalRow>
                      <ModalRow>
                        <span>일반상품</span>
                        <span>{(club.goodsTotal || 0).toLocaleString()}원</span>
                      </ModalRow>
                    </ModalClubBlock>
                  ))}
                </ModalList>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    )}
    </>
  );
};

export default CenterDashboardComponent;
